import { useEffect, useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation } from "@tanstack/react-query";
import { Link, createFileRoute } from "@tanstack/react-router";
import { ArrowLeft, CircleCheck } from "lucide-react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { apiUsersrequestResetPasswordFormatPostMutation } from "@/generated/api/@tanstack/react-query.gen";
import { handleFormError } from "@/lib/utils/handle-form-error.ts";
import { Button } from "@/components/ui/button.tsx";
import {
  CardAuth,
  CardAuthContent,
  CardAuthDescription,
  CardAuthFooter,
  CardAuthHeader,
  CardAuthTitle,
} from "@/components/ui/card-auth.tsx";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form.tsx";
import { Input } from "@/components/ui/input.tsx";
import { useAuthProvider } from "@/app/_providers/auth-provider.tsx";
import {
  ForgotPasswordFormData,
  forgotPasswordFormSchema,
} from "./_data/forgot-password-shema";

export const Route = createFileRoute("/(guest)/forgot-password/")({
  component: RouteComponent,
});

function RouteComponent() {
  const [isSubmitted, setIsSubmitted] = useState(false);
  const form = useForm({
    mode: "onChange",
    resolver: zodResolver(forgotPasswordFormSchema),
    defaultValues: {
      username: "",
    },
  });

  const auth = useAuthProvider();

  useEffect(() => {
    return () => {
      auth.logout();
    };
  }, []);

  const { mutate, isPending } = useMutation({
    ...apiUsersrequestResetPasswordFormatPostMutation(),
    onSuccess: () => {
      setIsSubmitted(true);
      toast.success("Email sent successfully");
    },
    onError: (error) => {
      handleFormError({ error, setError: form.setError });
    },
  });

  const onSubmit = (data: ForgotPasswordFormData) =>
    mutate({
      body: {
        username: data.username,
      },
    });

  return (
    <CardAuth>
      <CardAuthHeader className="text-center">
        <CardAuthTitle className="text-3xl font-bold">
          Forgot Password
        </CardAuthTitle>
        <CardAuthDescription>
          Enter your username, and we will send you an email with a link to
          reset your password
        </CardAuthDescription>
      </CardAuthHeader>
      <CardAuthContent>
        {!isSubmitted ? (
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
              <FormField
                control={form.control}
                name="username"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Username</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="Enter your username"
                        type="text"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <Button type="submit" className="w-full" disabled={isPending}>
                {isPending ? "Sending..." : "Send Reset Link"}
              </Button>
            </form>
          </Form>
        ) : (
          <div className="flex flex-col items-center space-y-6 space-x-2">
            <div className="flex flex-row items-start justify-start">
              <CircleCheck className="size-6 text-green-500" />
              <p className="text-center">
                We have sent you an email to reset your password. Please check
                your inbox.
              </p>
            </div>
            <Button
              variant="outline"
              className="w-full"
              onClick={() => onSubmit(form.getValues())}
            >
              Resend Email
            </Button>
          </div>
        )}
      </CardAuthContent>
      <CardAuthFooter>
        <Link
          to="/login"
          className="text-primary flex items-center text-sm hover:underline"
        >
          <ArrowLeft className="mr-2 size-4" />
          Back to Login
        </Link>
      </CardAuthFooter>
    </CardAuth>
  );
}
